import { z } from "zod";
import { agentOrchestrationContextSchema } from "./orchestration";

const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

export const agentTextBlockSchema = z.object({
  type: z.literal("text"),
  text: z.string().max(200_000),
});

export const agentImageBlockSchema = z.object({
  type: z.literal("image"),
  mediaType: z.enum(["image/png", "image/jpeg", "image/webp", "image/gif"]),
  dataBase64: z.string().max(Math.ceil(MAX_ATTACHMENT_BYTES / 3) * 4),
});

export const agentToolUseBlockSchema = z.object({
  type: z.literal("tool_use"),
  id: z.string().min(1).max(200),
  name: z.string().min(1).max(100),
  input: z.unknown(),
});

export const agentToolResultBlockSchema = z.object({
  type: z.literal("tool_result"),
  toolUseId: z.string().min(1).max(200),
  result: z.unknown(),
  isError: z.boolean().optional(),
});

/**
 * Opaque provider blocks are round-tripped intact. Anthropic puts compaction,
 * tool-search and MCP state in response.content, while OpenAI Responses returns
 * reasoning items that must accompany subsequent function outputs. Reducing
 * either provider response to text silently loses conversation state.
 */
export const agentProviderStateBlockSchema = z.object({
  type: z.literal("provider_state"),
  provider: z.enum(["anthropic", "openai"]),
  block: z.unknown(),
});

export const agentContentBlockSchema = z.discriminatedUnion("type", [
  agentTextBlockSchema,
  agentImageBlockSchema,
  agentToolUseBlockSchema,
  agentToolResultBlockSchema,
  agentProviderStateBlockSchema,
]);

export const agentMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.array(agentContentBlockSchema).min(1),
});

export const agentContextSchema = z.object({
  projectName: z.string().trim().min(1).max(160),
  sceneSummary: z.string().max(30_000),
  defaultWallHeightMm: z.number().min(100).max(100_000),
  defaultWallThicknessMm: z.number().min(1).max(10_000),
  scope: z.enum(["editor", "workspace"]).default("editor"),
  projectId: z.string().max(200).nullable().optional(),
  interaction: z.object({
    mode: z.literal("answer_only"),
    objective: z.string().trim().min(1).max(4_000),
    conditionalChecks: z.array(z.string().trim().min(1).max(1_000)).max(12).default([]),
    authorizedActions: z.array(z.string().trim().min(1).max(2_000)).max(12).default([]),
  }).optional(),
  orchestration: agentOrchestrationContextSchema.optional(),
});

export const agentToolDefinitionSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().min(1).max(4_000),
  inputSchema: z.record(z.string(), z.unknown()),
});

export const agentChatInputSchema = z.object({
  messages: z.array(agentMessageSchema).min(1).max(500),
  context: agentContextSchema,
  tools: z.array(agentToolDefinitionSchema).max(40).optional(),
  toolChoice: z.enum(["auto", "none", "required"]).default("auto"),
  toolPolicy: z.enum(["registry", "visible_only"]).default("registry"),
  forcedTool: z.string().min(1).max(120).optional(),
  effortHint: z.enum(["low", "medium", "high", "xhigh"]).optional()
    .describe("Esforço necessário nesta etapa; o servidor nunca ultrapassa o nível configurado pelo administrador."),
  maxOutputTokensHint: z.number().int().min(256).max(16_384).optional()
    .describe("Teto de saída desta etapa; o servidor nunca ultrapassa o limite suportado pelo modelo."),
});

export type AgentTextBlock = z.infer<typeof agentTextBlockSchema>;
export type AgentImageBlock = z.infer<typeof agentImageBlockSchema>;
export type AgentToolUseBlock = z.infer<typeof agentToolUseBlockSchema>;
export type AgentToolResultBlock = z.infer<typeof agentToolResultBlockSchema>;
export type AgentProviderStateBlock = z.infer<typeof agentProviderStateBlockSchema>;
export type AgentContentBlock = z.infer<typeof agentContentBlockSchema>;
export type AgentMessage = z.infer<typeof agentMessageSchema>;
export type AgentContext = z.infer<typeof agentContextSchema>;

export interface AgentModelResponse {
  role: "assistant";
  content: Array<AgentTextBlock | AgentToolUseBlock | AgentProviderStateBlock>;
}

export interface AgentUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens?: number;
  cachedInputTokens?: number;
  cacheCreationInputTokens?: number;
  reasoningTokens?: number;
}

export type AgentStreamEvent =
  | { type: "text_delta"; delta: string }
  | { type: "model_activity"; phase: "waiting" | "reasoning" | "tool_call"; toolName?: string }
  | { type: "skill_recommendations"; slugs: string[] }
  | { type: "context_compacted"; provider: "anthropic" | "openai"; removedMessages?: number }
  | { type: "message_end"; message: AgentModelResponse; model: string; usage?: AgentUsage; maxInputTokens?: number | null }
  | { type: "error"; message: string };
